import React from 'react';
import { useChangelogData } from './useChangelogData';
import { CATEGORIES } from './changelogTypes';
import ChangelogEntriesList from './ChangelogEntriesList';
import ChangelogDialogs from './ChangelogDialogs';

export const ChangelogManagementPanel: React.FC = () => {
  const model = useChangelogData();
  const {
    entries, loading, filterCategory, setFilterCategory, filterPublished, setFilterPublished,
    notification, setNotification, fetchEntries, openCreateForm, selectedEntries,
    setSelectedEntries, handleBulkToggle,
  } = model;

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div className="admin-notice-row" style={{ padding: 0, marginBottom: 16 }}
          role={notification.type === 'error' ? 'alert' : 'status'}>
          <div className={`admin-notice ${notification.type === 'success' ? 'admin-notice-success' : 'admin-notice-error'}`} style={{ maxWidth: '100%', flex: 1 }}>
            <span className="admin-notice-message flex-1">{notification.message}</span>
            <button onClick={() => setNotification(null)} aria-label="Dismiss notification" className="admin-notice-dismiss">×</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rp-bento-card mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>Changelog entries</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Published entries appear in the public release history. New manual entries start as drafts.
            </p>
            <p className="text-sm mt-1" style={{ color: 'var(--rp-text-tertiary)' }}>
              {entries.length} total · {entries.filter(e => e.is_published).length} published · {entries.filter(e => !e.is_published).length} drafts
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={openCreateForm}
              className="px-4 py-2 rounded-lg font-semibold text-sm transition-all"
              style={{ backgroundColor: 'var(--rp-primary)', color: '#fff' }}
            >
              + Add Entry
            </button>
            <button onClick={fetchEntries} disabled={loading}
              className="px-4 py-2 rounded-lg text-sm transition-all disabled:opacity-50"
              style={{ border: '1px solid var(--rp-border)', color: 'var(--rp-text)' }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters + Bulk Actions */}
      <div className="rp-bento-card mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-sm font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>Filter:</span>
          {/* Category filter */}
          <select value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            aria-label="Filter by category"
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text)', border: '1px solid var(--rp-border)' }}
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {/* Published filter */}
          <select value={filterPublished}
            onChange={e => setFilterPublished(e.target.value)}
            aria-label="Filter by publish status"
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text)', border: '1px solid var(--rp-border)' }}
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Bulk actions */}
          {selectedEntries.size > 0 && (
            <div className="flex gap-2 items-center">
              <span className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>{selectedEntries.size} selected</span>
              <button onClick={() => handleBulkToggle(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ backgroundColor: 'rgba(16,185,129,0.2)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}
              >
                Publish
              </button>
              <button onClick={() => handleBulkToggle(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ backgroundColor: 'rgba(245,158,11,0.2)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
              >
                Unpublish
              </button>
              <button onClick={() => setSelectedEntries(new Set())}
                className="px-3 py-1.5 rounded-lg text-xs"
                style={{ color: 'var(--rp-text-secondary)' }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      <ChangelogEntriesList model={model} />
      <ChangelogDialogs model={model} />
    </div>
  );
};
